// Trigger jedné top-level sekce v liště + její popover. Poskytuje section-bound
// MenuConfigContext (akce onOpen/onDetach nesou kontext sekce).

import { useMemo, useRef, useState } from 'react';

import type { DensityTokens } from './density';
import { MenuConfigContext, type MenuConfig } from './menuConfigContext';
import { MenuPopover } from './MenuPopover';
import type {
  MenuActionContext,
  MenuDensity,
  MenuIconResolver,
  MenuLoadViz,
  MenuSection,
  MenuSepStyle,
} from './menuTypes';

export interface MenuBarButtonProps {
  readonly section: MenuSection;
  readonly density: MenuDensity;
  readonly densityTokens: DensityTokens;
  readonly separators: MenuSepStyle;
  readonly detachAlways: boolean;
  readonly loading: boolean;
  readonly loadViz: MenuLoadViz;
  readonly iconForNode: MenuIconResolver;
  readonly onOpen: (ctx: MenuActionContext) => void;
  readonly onDetach: (ctx: MenuActionContext) => void;
}

export function MenuBarButton({
  section,
  density,
  densityTokens,
  separators,
  detachAlways,
  loading,
  loadViz,
  iconForNode,
  onOpen,
  onDetach,
}: MenuBarButtonProps) {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);

  const config = useMemo<MenuConfig>(
    () => ({
      density,
      densityTokens,
      separators,
      detachAlways,
      iconForNode,
      onOpen: (node) => {
        onOpen({ node, section });
        setOpen(false);
      },
      onDetach: (node) => {
        onDetach({ node, section });
        setOpen(false);
      },
    }),
    [density, densityTokens, separators, detachAlways, iconForNode, onOpen, onDetach, section]
  );

  return (
    <MenuConfigContext.Provider value={config}>
      <button
        ref={triggerRef}
        type="button"
        className="legacy-menu__item"
        data-active={open ? 'true' : undefined}
        data-testid={`menu-section-${section.id}`}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
      >
        {section.label}
      </button>
      {open && (
        <MenuPopover
          section={section}
          anchorRef={triggerRef}
          loading={loading}
          loadViz={loadViz}
          onClose={() => setOpen(false)}
        />
      )}
    </MenuConfigContext.Provider>
  );
}
